import { motion } from "framer-motion";
import { CheckCircle2, Type, Zap, Shield, Layers, Target } from "lucide-react";
import { useState, useEffect } from "react";
import { ContactModal } from "@/components/ContactModal";
import { useScrollAnimations } from "@/components/GSAPScrollAnimations";

const TypesOfLogosExplained = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollContainer = useScrollAnimations();

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Types of Logos Explained",
      "description": "Explore different logo types including wordmarks, symbols, combination marks, and emblems. Learn when to use each type for your brand.",
      "author": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "url": "https://koech-creatives.onrender.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "logo": {
          "@type": "ImageObject",
          "url": "https://koech-creatives.onrender.com/og-image.png"
        }
      },
      "datePublished": "2026-07-13",
      "dateModified": "2026-07-13",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://koech-creatives.onrender.com/types-of-logos-explained"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <main ref={scrollContainer} className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Types of Logos Explained
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            className="text-xl text-muted-foreground mb-8"
          >
            Explore the different types of logos and learn when to use each one. Understanding logo types helps you choose the right style for your brand and communicate effectively with your audience.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get Your Logo Designed
          </motion.button>
        </div>
      </section>

      {/* Wordmarks */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Wordmarks
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Wordmarks are text-based logos that use the company name as the primary design element. They focus on typography to create a distinctive visual identity without additional symbols or icons.
          </motion.p>
          <div className="bg-card p-6 rounded-lg border border-gray-800 mb-6">
            <h3 className="font-semibold text-lg mb-4 text-coral">Best For:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Companies with short, memorable names</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Brands seeking simplicity and clarity</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Professional services and consulting firms</li>
            </ul>
          </div>
          <p className="text-muted-foreground">Examples: Google, Coca-Cola, Facebook, Visa</p>
        </div>
      </section>

      {/* Emblem Logos */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Emblem Logos
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Emblem logos feature text inside a symbol or badge, often resembling a crest, seal, or coat of arms. They convey tradition, authority, and heritage, making them popular with established institutions.
          </motion.p>
          <div className="bg-card p-6 rounded-lg border border-gray-800 mb-6">
            <h3 className="font-semibold text-lg mb-4 text-coral">Best For:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Government agencies and organizations</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Educational institutions</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Luxury brands seeking prestige</li>
            </ul>
          </div>
          <p className="text-muted-foreground">Examples: Starbucks, NFL, Harley-Davidson, UPS</p>
        </div>
      </section>

      {/* Abstract Logos */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Abstract Logos
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Abstract logos use geometric shapes and forms to represent the brand without literal imagery. They rely on symbolism and visual metaphor to communicate brand values and personality.
          </motion.p>
          <div className="bg-card p-6 rounded-lg border border-gray-800 mb-6">
            <h3 className="font-semibold text-lg mb-4 text-coral">Best For:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Technology companies and startups</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Brands seeking modern, innovative image</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Companies with abstract brand concepts</li>
            </ul>
          </div>
          <p className="text-muted-foreground">Examples: Nike swoosh, Apple, Pepsi, Adidas</p>
        </div>
      </section>

      {/* Mascot Logos */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Mascot Logos
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Mascot logos feature illustrated characters that represent the brand. These characters create personality, warmth, and emotional connections with audiences, making them particularly effective for family-oriented brands.
          </motion.p>
          <div className="bg-card p-6 rounded-lg border border-gray-800 mb-6">
            <h3 className="font-semibold text-lg mb-4 text-coral">Best For:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Food and beverage companies</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Sports teams and entertainment brands</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Brands targeting families and children</li>
            </ul>
          </div>
          <p className="text-muted-foreground">Examples: KFC's Colonel, GEICO Gecko, Pillsbury Doughboy, Michelin Man</p>
        </div>
      </section>

      {/* Combination Logos */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Combination Logos
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Combination logos combine text and symbols to create a versatile brand identity. They offer the flexibility of using elements separately or together, making them adaptable to various applications.
          </motion.p>
          <div className="bg-card p-6 rounded-lg border border-gray-800 mb-6">
            <h3 className="font-semibold text-lg mb-4 text-coral">Best For:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Most business types and industries</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Brands seeking maximum versatility</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Companies with both name and symbol recognition needs</li>
            </ul>
          </div>
          <p className="text-muted-foreground">Examples: Adidas, Burger King, Taco Bell, Lacoste</p>
        </div>
      </section>

      {/* Minimalist Logos */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Minimalist Logos
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-lg text-muted-foreground mb-6"
          >
            Minimalist logos use simple, clean designs with limited elements and colors. They focus on essential shapes and typography to create elegant, memorable identities that work across all mediums.
          </motion.p>
          <div className="bg-card p-6 rounded-lg border border-gray-800 mb-6">
            <h3 className="font-semibold text-lg mb-4 text-coral">Best For:</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Modern tech companies</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Luxury and fashion brands</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-coral" /> Brands seeking sophistication and elegance</li>
            </ul>
          </div>
          <p className="text-muted-foreground">Examples: Chanel, Apple, Airbnb, Mastercard</p>
        </div>
      </section>

      {/* Choosing the Right Type */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            How to Choose the Right Logo Type
          </motion.h2>
          <div className="space-y-4">
            {[
              { factor: "Industry Norms", desc: "Consider what's typical in your industry while staying unique." },
              { factor: "Brand Personality", desc: "Match the logo type to your brand's character and values." },
              { factor: "Target Audience", desc: "Choose a style that resonates with your ideal customers." },
              { factor: "Application Needs", desc: "Consider where and how the logo will be used." },
              { factor: "Scalability", desc: "Ensure the chosen type works at all sizes." },
              { factor: "Longevity", desc: "Select a type that will remain relevant over time." },
            ].map((item, index) => (
              <motion.div
                key={item.factor}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <Target className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.factor}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Related Resources
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Ultimate Guide to Logo Design Services", url: "/logo-design-services-guide", desc: "Comprehensive guide to professional logo design" },
              { title: "What Makes a Good Logo", url: "/what-makes-a-good-logo", desc: "Learn the essential qualities of effective logos" },
              { title: "Minimalist Logo Design Guide", url: "/minimalist-logo-design-guide", desc: "Create powerful minimalist logos" },
              { title: "Logo Design Principles", url: "/logo-design-principles", desc: "Master the fundamental design principles" },
            ].map((resource, index) => (
              <motion.a
                key={resource.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                href={resource.url}
                className="bg-card p-6 rounded-lg border border-gray-800 hover:border-coral transition-colors block"
              >
                <h3 className="font-semibold text-lg mb-2">{resource.title}</h3>
                <p className="text-muted-foreground">{resource.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Need Help Choosing Your Logo Type?
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-xl text-muted-foreground mb-8"
          >
            Our expert designers can help you select the perfect logo type for your brand and create a stunning visual identity.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get a Free Consultation
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default TypesOfLogosExplained;
